import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Hexagon, FileText, Tags, FileCheck, BellRing,
  Database, FolderCog, ShieldCheck, Clock, Globe2, Sparkles,
} from "lucide-react";
import { IMAGES, getServices, getWhyUs, getValues } from "@/lib/siteContent";
import { getBlogPosts } from "@/lib/blogContent";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = { Hexagon, FileText, Tags, FileCheck, BellRing, Database, FolderCog };

const Home = () => {
  const { language, t } = useLanguage();
  const SERVICES = getServices(language);
  const WHY_US = getWhyUs(language);
  const VALUES = getValues(language);
  const posts = getBlogPosts(language).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div
          className="absolute inset-0 hex-pattern opacity-100 pointer-events-none"
          aria-hidden="true"
        />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-teal-100/60 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-emerald-100/40 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-teal-800 text-xs font-medium tracking-wide">
              <Sparkles size={13} />
              {t("home.badge")}
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] text-slate-900 tracking-tight">
              {t("home.heroTitleLine1")}
              <br />
              {t("home.heroTitlePrefix")} <em className="not-italic text-teal-700">{t("home.heroTitleEm")}</em> {t("home.heroTitleSuffix")}
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("home.heroLead")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/hizmetler"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-teal-700 text-white text-sm font-medium hover:bg-teal-800 transition-colors"
                data-testid="hero-cta-services"
              >
                {t("home.ctaServices")}
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/iletisim"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-slate-300 text-slate-800 text-sm font-medium hover:border-teal-700 hover:text-teal-700 transition-colors"
                data-testid="hero-cta-quote"
              >
                {t("home.ctaQuote")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-teal-100 to-emerald-50 -z-10" />
              <img
                src={IMAGES.heroPortrait}
                alt={t("home.heroImageAlt")}
                className="w-full rounded-2xl object-cover aspect-[4/5] shadow-xl shadow-teal-900/10"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-4 shadow-lg max-w-xs hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-700">
                    <ShieldCheck size={18} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("home.heroCardKicker")}</div>
                    <div className="text-sm font-medium text-slate-900">{t("home.heroCardTitle")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="relative py-24 lg:py-32 bg-white" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-3">
                {t("home.servicesKicker")}
              </div>
              <h2 className="font-display text-3xl lg:text-5xl text-slate-900 tracking-tight">
                {t("home.servicesTitlePrefix")} <em className="not-italic text-teal-700">{t("home.servicesTitleEm")}</em>.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600">
                {t("home.servicesLead")}
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = ICONS[s.icon] || Hexagon;
              return (
                <Link
                  key={s.slug}
                  to={`/hizmetler/${s.slug}`}
                  className="group flex flex-col rounded-xl bg-white border border-slate-200 overflow-hidden card-hover"
                  data-testid={`home-service-card-${idx}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 h-10 w-10 rounded-lg bg-white/90 backdrop-blur text-teal-700 flex items-center justify-center border border-teal-100">
                      <Icon size={18} />
                    </div>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display text-xl text-slate-900 leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
                      {s.short}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-teal-700">
                      {t("home.serviceDetailLink")} <ArrowRight size={13} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-24 lg:py-32 bg-[#F0FDFA]" data-testid="home-why-us">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-3">
              {t("home.whyKicker")}
            </div>
            <h2 className="font-display text-3xl lg:text-5xl text-slate-900 tracking-tight">
              {t("home.whyTitlePrefix")} <em className="not-italic text-teal-700">{t("home.whyTitleEm")}</em> {t("home.whyTitleSuffix")}
            </h2>
            <p className="mt-5 text-slate-600 max-w-md">
              {t("home.whyLead")}
            </p>
            <div className="mt-8 flex flex-col gap-3 max-w-sm">
              <div className="flex items-center gap-3 text-sm text-slate-700">
                <Clock size={16} className="text-teal-700" /> {t("home.whyPoint1")}
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-700">
                <Globe2 size={16} className="text-teal-700" /> {t("home.whyPoint2")}
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-700">
                <ShieldCheck size={16} className="text-teal-700" /> {t("home.whyPoint3")}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {WHY_US.map((w, idx) => (
              <div
                key={w.title}
                className="p-6 rounded-xl bg-white border border-slate-200 card-hover"
                data-testid={`why-us-${idx}`}
              >
                <div className="text-2xl font-display text-teal-700">0{idx + 1}</div>
                <div className="mt-3 font-medium text-slate-900">{w.title}</div>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="py-20 border-y border-slate-100 bg-white" data-testid="home-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-center justify-between">
            <div className="font-display text-2xl lg:text-3xl text-slate-900 max-w-md tracking-tight">
              {t("home.valuesTitle")}
            </div>
            <ul className="flex flex-wrap gap-x-8 gap-y-3">
              {VALUES.map((v) => (
                <li key={v} className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-600" />
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-24 lg:py-32 bg-white" data-testid="home-blog">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-3">{t("home.blogKicker")}</div>
              <h2 className="font-display text-3xl lg:text-5xl text-slate-900 tracking-tight max-w-xl">
                {t("home.blogTitlePrefix")} <em className="not-italic text-teal-700">{t("home.blogTitleEm")}</em>.
              </h2>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800"
              data-testid="home-blog-all"
            >
              {t("home.blogAll")} <ArrowRight size={14} />
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-80 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((p, idx) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col rounded-xl overflow-hidden bg-white border border-slate-200 card-hover"
                  data-testid={`home-blog-card-${idx}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] uppercase tracking-[0.12em] text-teal-800 border border-teal-100">
                      {p.category}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{p.published_at}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> {p.read_minutes} {t("home.blogMinutes")}
                      </span>
                    </div>
                    <h3 className="mt-3 font-display text-xl text-slate-900 leading-snug">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
                      {p.excerpt}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700">
                      {t("home.blogRead")} <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-28 bg-slate-900 text-white overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 hex-pattern opacity-[0.08]" aria-hidden="true" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[800px] rounded-full bg-teal-700/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.22em] text-teal-300">
            {t("home.ctaKicker")}
          </div>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl tracking-tight">
            {t("home.ctaTitle")}
          </h2>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
            {t("home.ctaLead")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-teal-500 text-slate-900 text-sm font-semibold hover:bg-teal-400 transition-colors"
              data-testid="cta-quote-button"
            >
              {t("home.ctaQuote")}
              <ArrowRight size={16} />
            </Link>
            <a
              href="https://wa.me/902123333333"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors"
              data-testid="cta-whatsapp-button"
            >
              {t("home.ctaWhatsapp")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
